import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FiUser, FiLock, FiShield, FiArrowRight } from 'react-icons/fi'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../contexts/AuthContext.jsx'

const Login = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { user, login } = useAuth()

  if (user) {
    return <Navigate to="/admin" replace />
  }

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const result = await login(credentials)
    
    if (!result.success) {
      setError(result.error)
    }
    
    setLoading(false)
  }

  return (
    <>
      <Helmet>
        <title>Login - Portfolio Admin</title>
        <meta name="description" content="Login to access the admin dashboard" />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-dark-900 relative overflow-hidden">
        {/* Decorative Background Glows */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-secondary_accent/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-md w-full relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="glass-effect p-8 sm:p-10 rounded-[2.5rem] shadow-2xl border border-white/5 dark:border-white/10 relative overflow-hidden group"
          >
            {/* Background Icon Decoration */}
            <div className="absolute -right-8 -top-8 opacity-[0.03] group-hover:opacity-[0.06] transition-all duration-700 group-hover:scale-125 group-hover:-rotate-12 pointer-events-none">
              <FiShield size={220} />
            </div>

            <div className="relative z-10">
              <div className="text-center mb-10">
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary_accent/10 text-secondary_accent mb-6"
                >
                  <FiLock size={32} />
                </motion.div>
                <h2 className="text-3xl font-bold text-white tracking-tight font-clash">Admin Portal</h2>
                <p className="mt-3 text-gray-400 font-light">
                  Secure access for authorized administrators
                </p>
              </div>

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">
                      Identity
                    </label>
                    <div className="relative group/input">
                      <FiUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 group-focus-within/input:text-secondary_accent transition-colors" />
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="w-full bg-white/5 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-secondary_accent/20 focus:border-secondary_accent/40 transition-all duration-300"
                        placeholder="admin@portfolio.com"
                        value={credentials.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500 ml-1">
                      Security Key
                    </label>
                    <div className="relative group/input">
                      <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 group-focus-within/input:text-secondary_accent transition-colors" />
                      <input
                        id="password"
                        name="password"
                        type="password"
                        required
                        className="w-full bg-white/5 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-secondary_accent/20 focus:border-secondary_accent/40 transition-all duration-300"
                        placeholder="••••••••••••"
                        value={credentials.password}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                {error && (
                  <motion.div 
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center font-medium"
                  >
                    {error}
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-2xl bg-secondary_accent hover:bg-white text-dark-900 font-bold uppercase tracking-widest transition-all duration-300 shadow-lg shadow-secondary_accent/10 flex items-center justify-center gap-2 group/btn disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-dark-900/30 border-t-dark-900 rounded-full animate-spin" />
                  ) : (
                    <>
                      Verify Access
                      <FiArrowRight className="group-hover/btn:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="text-center mt-8 text-gray-600 text-xs tracking-[0.2em] uppercase"
          >
            © {new Date().getFullYear()} Osman Ghani Granday • System Secure
          </motion.p>
        </div>
      </div>
    </>
  );
}

export default Login